import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import {
  AppContent,
  AppPage,
  DeactivatedLozenge,
  DeletedLozenge,
  ActiveLozenge,
  Tabs,
  TabPanel,
  ErrorComponents,
  useRecentlyViewed,
  useFeatureToggle,
} from '@css-xp/base';
import { getEntityName, getCompanyNameRoute } from '@css-xp/ppc-utils';
import {
  PPC_PAGE_TITLE,
  RecentlyViewedEntityEnum,
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  SUBSCRIPTION_CONSTANTS,
  refundedTabNameKey,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { CloudSubscription } from '@atlassian/cssxp-typescommon';
import { getEntitlementName } from '@atlassian/temp-helper';
import { FetchCloudLicenseQuery, FetchCloudLicenseQueryVariables } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { Background } from '@css-xp/page-background/src/background-image';
import { BillingStatus } from '@css-xp/graphtypes';
import { removeContentScrollingHack } from '@atlassian/cssxp-utils-generic';
import { fetchCloudLicense } from '@css-xp/cloud-site-v2';
import { InvoiceTable } from '@css-xp/ppc-invoice-table';
import { IncludedProductsSidebar, SubscriptionTableFlatContainer } from '@css-xp/ppc-subscription-table';
import { OrderHistoryTable } from '@css-xp/ppc-order-history-table';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { CloudSitePageLoader } from '@css-xp/ppc-page-loaders';
import './cloud-site-page.scss';

export const CloudSitePage = () => {
  const history = useHistory();
  const { txaId, cloudSiteId: id } = useParams<{ txaId: string; cloudSiteId: string }>();
  const { updateRecentlyViewed } = useRecentlyViewed();
  const [isEnabled] = useFeatureToggle();
  const isNewTab = !window.history.state;
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isInternalUsageLicensesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INTERNAL_USAGE_LICENSES_SUPPORT);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isFutureDatedSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS
  );
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);

  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const cloudLicenseResult = useQuery<FetchCloudLicenseQuery, FetchCloudLicenseQueryVariables>(fetchCloudLicense, {
    variables: {
      params: {
        id: id,
        transactionAccountId: txaId,
      },
    },
    skip: !id || !txaId,
  });

  const { loading, error, data } = cloudLicenseResult;
  const cloudSite = data?.cloudLicense as CloudSubscription;
  // tracking the recently viewed cloud-sites
  useEffect(() => {
    if (error || loading || !data) {
      return;
    }
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.CLOUD_SITE, txaId),
      metadata: {
        id: id,
        cloudSiteUrl: cloudSite?.cloudSiteUrl,
        companyName: cloudSite?.shipToParty?.name,
      },
    });
  }, [data, error, id, loading, txaId, updateRecentlyViewed]);

  useTitle(data?.cloudLicense?.accountName ?? '', PPC_PAGE_TITLE.SUBSCRIPTIONS);

  if (loading) {
    if (isDesignIteration1Enabled) {
      return <CloudSitePageLoader />;
    }

    return <LoadingIndicator />;
  }

  if (error) {
    // Redirect to not found page if entitlement ID does not belong to the TXA
    const notFoundText = 'not found';
    if (error?.message.toLowerCase().indexOf(notFoundText) > -1) {
      history.push(routePaths.notFound);
    }
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error} />;
  }

  if (!cloudSite) {
    return null;
  }
  const { billingStatus, sen, baseProduct, cloudSiteId } = cloudSite;

  const allowedSidebars = {
    [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
  };

  const cloudSiteIdFilter = [
    {
      key: 'cloudSiteId',
      value: cloudSiteId,
    },
  ];
  const cloudSiteIdInvoiceFilter = [
    {
      cloudSiteId: cloudSiteId,
    },
  ];

  const getPageHeading = () => {
    return (
      <div className="page-heading">
        <h1 className="page-header-text">
          <span>
            {baseProduct} - {getEntitlementName(cloudSite)}
          </span>
          {billingStatus === BillingStatus.SUSPENDED && <DeactivatedLozenge />}
          {billingStatus === BillingStatus.DELETED && <DeletedLozenge />}
          {billingStatus === BillingStatus.ACTIVE && <ActiveLozenge />}
        </h1>
      </div>
    );
  };

  const getBreadcrumbItems = () => {
    let pathToCustomer = routePaths.customerAddresses;
    if (cloudSite?.shipToParty?.name) {
      pathToCustomer = getCompanyNameRoute({
        companyName: cloudSite?.shipToParty?.name,
      });
    }

    return [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Customers',
        href: routePaths.customerAddresses,
      },
      {
        title: cloudSite?.shipToParty?.name || 'Details',
        href: pathToCustomer,
      },
      {
        title: cloudSite?.cloudSiteUrl || `SEN-${sen}`,
        href: routePaths.cloudSite,
      },
    ];
  };

  function renderOrderHistoryTable() {
    const orderHistoryFilterOptions = [
      {
        key: 'orderLineItemsCloudSiteId',
        value: id,
      },
    ];
    return (
      <OrderHistoryTable label={'Order history'} filters={orderHistoryFilterOptions} transactionAccountId={txaId} />
    );
  }

  const renderAppPage = () => {
    return (
      <AppPage
        id={'cloud-site-detail-page'}
        allowedSidebars={allowedSidebars}
        className="app-page-v2 cloud-site-detail-page"
      >
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_CLOUD_SITE_SCREEN,
            attributes: {},
          }}
        />
        <AppContent>
          <div className="cloud-site-details-container">
            <div data-testid="cloud-site-detail-tabs">
              <Tabs className="tab-container">
                <TabPanel label="Subscriptions" enabled={true}>
                  <SubscriptionTableFlatContainer
                    transactionAccountId={txaId}
                    filterOptions={cloudSiteIdFilter}
                    label="Subscriptions"
                    shouldHideCustomiseView={isDesignIteration1Enabled}
                    shouldHideTabs={isDesignIteration1Enabled}
                    isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                    isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
                    isSupportForExternallyBilledSubscriptionsEnabled={true}
                    isTWCPhase1Enabled={isTWCPhase1Enabled}
                    isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
                    columnsToShow={[
                      SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
                      SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT,
                    ]}
                    showEnterpriseInstances={isMultiInstanceAppsEnabled}
                  />
                </TabPanel>
                <TabPanel label="Invoices" enabled={true}>
                  <InvoiceTable
                    label="Invoices"
                    transactionAccountId={txaId}
                    id={txaId}
                    filters={cloudSiteIdInvoiceFilter}
                    shouldHideCustomiseView={isDesignIteration1Enabled}
                  />
                </TabPanel>
                {isDesignIteration1Enabled && (
                  <TabPanel label="Refunds" enabled={true}>
                    <InvoiceTable
                      label="Refunds"
                      transactionAccountId={txaId}
                      id={txaId}
                      filters={cloudSiteIdInvoiceFilter}
                      shouldHideCustomiseView={true}
                      selectedInterfaceKey={refundedTabNameKey}
                    />
                  </TabPanel>
                )}
                <TabPanel label="Order history" enabled={true}>
                  {renderOrderHistoryTable()}
                </TabPanel>
              </Tabs>
            </div>
          </div>
        </AppContent>
      </AppPage>
    );
  };

  return isDesignIteration1Enabled ? (
    <PageBackgroundV2
      heading={getPageHeading()}
      className="cloud-site-detail-v2"
      breadcrumbsProps={{ items: getBreadcrumbItems() }}
    >
      {renderAppPage()}
    </PageBackgroundV2>
  ) : (
    <PageBackground
      heading={getPageHeading()}
      imageKey={Background.CLOUD_SITE_BACKGROUND}
      className="page-background cloud-site-detail"
      isBackButtonEnabled={!isNewTab}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
